import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { ProgressSpinner } from 'primereact/progressspinner'
import { classNames } from 'primereact/utils'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useRecoilState, useRecoilValue } from 'recoil'
import { apiClient } from '../../api/configs/axios-setup'
import { GifJob, listGifs } from '../../api/gif-API'
import { useGetHeadlineData, useGetHeadlineDataProjectRun } from '../../api/report-API'
import { useGetRunsOverview } from '../../api/report-API'
import {
  createReport,
  deleteReport,
  exportReportPptx,
  getReportJob,
  getRunGeometryImages,
  listReports,
  previewReport,
  ReportDeltaMetric,
  ReportJob,
  ReportMavMediaItem,
  ReportMediaRef,
  ReportPayload,
  ReportRunCase,
  ReportRunSelection,
  ReportSectionInput,
  updateReport,
  uploadSectionMedia
} from '../../api/report-generator-API'
import {
  applyMetricFilterParams,
  PostProcImage,
  PostProcImageFilters,
  PostProcImagesResponse
} from '../../api/submission-API'
import { addRunMedia } from '../../api/run-media-API'
import { downloadFileDirectFromBlob } from '../../services/blobUploadService'
import { currentUserState, reportGeneratorRunState } from '../../utils/state-atoms'
import { canMutate } from '../../utils/roles'
import { UploadTypes } from '../../utils/constants'
import MarkupModal, { MarkupImage } from './MarkupModal'
import MetricMultiSelect from './MetricMultiSelect'

const POLL_INTERVAL_MS = 1500
const MAX_IMAGES_PER_CASE = 200

// Stable id for the auto-generated geometry-change section so run reloads replace it.
const GEOMETRY_SECTION_ID = 'geometry-changes'
// Stable id for the marked-up images section.
const MARKUP_SECTION_ID = 'markup'
// Delta metric fields shown as editable columns, mirroring the Headline Data grid.
const DELTA_FIELDS: { key: keyof ReportDeltaMetric; label: string }[] = [
  { key: 'delta_cd', label: 'ΔCD' },
  { key: 'delta_cl', label: 'ΔCL' },
  { key: 'delta_clf', label: 'ΔCLF' },
  { key: 'delta_clr', label: 'ΔCLR' },
  { key: 'delta_csf', label: 'ΔCSF' },
  { key: 'delta_csr', label: 'ΔCSR' },
  { key: 'delta_pct_clf', label: 'Δ%CLF' }
]

/** A case (state) of the loaded run; drives the delta table and media browsing. */
type ReportCase = {
  gmId: string
  systemRunId: string
  refGmId?: string
  stateName?: string
  projectName?: string
  runName?: string
  yaw?: number
  steer?: number
  roll?: number
  speed?: number
  highlighted?: boolean
  delta: ReportDeltaMetric
}

/** A section being edited in the form. */
type EditSection = {
  id: string
  heading: string
  /** One comment per line. */
  commentsText: string
  recommendation: string
  images: ReportMediaRef[]
  gifs: ReportMediaRef[]
  /** Anchor target for a consolidated report's first section in a weekly summary. */
  anchorId?: string
  /** Per-report delta rows, shown as a table inside the section (summary decks). */
  deltaMetrics?: ReportDeltaMetric[]
}

const toNum = (v: unknown): number | undefined => {
  const n = typeof v === 'string' ? parseFloat(v) : (v as number)
  return typeof n === 'number' && !Number.isNaN(n) ? n : undefined
}

const fmtDate = (ms?: number): string =>
  ms ? new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'

// Display formatting for the read-only delta table (Δ%CLF as a percentage).
const fmtDelta = (
  key: keyof ReportDeltaMetric,
  v: number | string | boolean | undefined
): string => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN
  if (Number.isNaN(n)) return '—'
  return key === 'delta_pct_clf' ? `${n.toFixed(2)}%` : n.toFixed(3)
}

// Conditional delta coloring, matching the Headline Data grid: negative is green,
// positive is red, near-zero is muted, exact zero is ink, and Δ%CLF is never colored.
const deltaColorClass = (
  key: keyof ReportDeltaMetric,
  v: number | string | boolean | undefined
): string => {
  if (key === 'delta_pct_clf') return ''
  const n = typeof v === 'number' ? v : v != null ? Number(v) : NaN
  if (Number.isNaN(n)) return 'text-gray-400'
  if (n === 0) return 'text-black dark:!text-neutral-60'
  if (Math.abs(n) < 0.001) return 'text-neutral-60 dark:!text-neutral-60'
  return n < 0 ? 'text-green-600' : 'text-red-600'
}

const newSection = (): EditSection => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  heading: '',
  commentsText: '',
  recommendation: '',
  images: [],
  gifs: []
})

// Geometry-change images live in the cfd-data container, which the browser cannot
// reach directly. Fetch the bytes through the DownloadBlobProxy and render them
// from an object URL instead of loading the source SAS URL in an <img> tag.
const GeometryProxyImage: FC<{ blobPath?: string; gmid?: string; alt?: string }> = ({
  blobPath,
  gmid,
  alt
}) => {
  const [src, setSrc] = useState('')
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (!blobPath) {
      setFailed(true)
      return
    }
    let objectUrl = ''
    let cancelled = false
    downloadFileDirectFromBlob(
      async () => '',
      UploadTypes.GEOMETRY_CHANGE_FILE,
      blobPath,
      null,
      gmid
    )
      .then(blob => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setSrc(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [blobPath, gmid])
  if (failed) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-neutral-70 text-[10px] text-gray-400">
        Unavailable
      </div>
    )
  }
  if (!src) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-neutral-70">
        <ProgressSpinner style={{ width: '1.25rem', height: '1.25rem' }} strokeWidth="6" />
      </div>
    )
  }
  return <img src={src} alt={alt} className="w-full h-full object-cover" draggable={false} />
}

/** Post-proc image filters mirroring the Headline Data gallery. */
type PickerFilters = {
  imageType?: string | null
  section?: string | null
  location?: string | null
  /** Plain metrics, or "{metric}||{referenceRunId}" entries for compare deltas. */
  metricTypes?: string[] | null
}

const fetchCaseImages = async (
  systemRunId: string,
  filters: PickerFilters = {},
  limit = MAX_IMAGES_PER_CASE
): Promise<PostProcImagesResponse> => {
  const params = new URLSearchParams({ skip: '0', limit: String(limit) })
  if (filters.imageType) params.set('imageType', filters.imageType)
  if (filters.section) params.set('section', filters.section)
  if (filters.location) params.set('location', filters.location)
  applyMetricFilterParams(params, filters.metricTypes)
  const res = await apiClient.get<PostProcImagesResponse>(
    `/WorkflowSubmissions/cases/${encodeURIComponent(systemRunId)}/images?${params.toString()}`
  )
  return res.data
}

/** Map a run case (from Headline Data) into the editable report-case model. */
const runCaseToReportCase = (project: string, runName: string, c: ReportRunCase): ReportCase => ({
  gmId: c.gmId,
  systemRunId: c.systemRunId || `0-${c.gmId}`,
  refGmId: c.refGmId,
  stateName: c.stateName,
  projectName: project,
  runName,
  yaw: c.yaw,
  steer: c.steer,
  roll: c.roll,
  speed: c.speed,
  delta: {
    caseName: c.stateName || c.gmId,
    gm_id: c.gmId,
    ref_gm_id: c.refGmId,
    delta_cd: c.delta_cd,
    delta_cl: c.delta_cl,
    delta_clf: c.delta_clf,
    delta_clr: c.delta_clr,
    delta_csf: c.delta_csf,
    delta_csr: c.delta_csr,
    delta_pct_clf: c.delta_pct_clf
  }
})

/** Rebuild editable cases from a saved report payload (delta rows align by index). */
const casesFromPayload = (p?: ReportPayload): ReportCase[] => {
  const deltas = p?.deltaMetrics ?? []
  return (p?.cases ?? []).map((c, i) => {
    const delta = deltas[i] ?? { caseName: c.caseName }
    const gmId = c.gm_id ?? delta.gm_id ?? ''
    return {
      gmId,
      systemRunId: c.systemRunId || `0-${gmId}`,
      refGmId: delta.ref_gm_id,
      stateName: c.caseName,
      projectName: c.projectName,
      runName: c.runName,
      yaw: c.yaw,
      steer: c.steer,
      roll: c.roll,
      speed: c.speed,
      highlighted: delta.highlight,
      delta: { ...delta, caseName: c.caseName }
    }
  })
}

/** Map the raw Databricks headline-data response into flat row objects. */
const parseHeadlineRows = (raw: any): Record<string, any>[] => {
  const cols = raw?.manifest?.schema?.columns
  const arr = raw?.result?.data_array
  if (!Array.isArray(cols) || !Array.isArray(arr)) return []
  return arr.map((row: any[]) => {
    const o: Record<string, any> = {}
    cols.forEach((c: any) => {
      o[c.name] = row[c.position]
    })
    return o
  })
}

const ReportGenerator: FC = () => {
  const currentUser = useRecoilValue(currentUserState)
  const userCanMutate = canMutate(currentUser?.role?.roleId)
  const [title, setTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [author, setAuthor] = useState(() =>
    [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ')
  )

  const [cases, setCases] = useState<ReportCase[]>([])
  const [runInfo, setRunInfo] = useState<{ project: string; runName: string } | null>(null)
  // Titles of the source reports when editing a consolidated weekly summary.
  const [summaryReportNames, setSummaryReportNames] = useState<string[]>([])
  // External media pushed by MAV (read-only in the editor).
  const [mavMedia, setMavMedia] = useState<ReportMavMediaItem[]>([])
  const [mavNotes, setMavNotes] = useState<string[]>([])

  // In-tab run selection.
  const [projectRunPairs, setProjectRunPairs] = useState<{ project: string; run: string }[]>([])
  const [selectedProject, setSelectedProject] = useState<string | null>(null)
  const [selectedRun, setSelectedRun] = useState<string | null>(null)
  const [loadingRun, setLoadingRun] = useState(false)

  const [, getProjectRun] = useGetHeadlineDataProjectRun()
  const [, getHeadlineData] = useGetHeadlineData()

  // Handoff from Headline Data right-click "Generate Report".
  const [runHandoff, setRunHandoff] = useRecoilState(reportGeneratorRunState)

  const [sections, setSections] = useState<EditSection[]>([newSection()])

  const [job, setJob] = useState<ReportJob | null>(null)
  const [generating, setGenerating] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [exportingPptx, setExportingPptx] = useState(false)
  const pollRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Recent Reports list view.
  const [view, setView] = useState<'editor' | 'list'>('list')
  const [editingReportId, setEditingReportId] = useState<string | null>(null)
  const [reports, setReports] = useState<ReportJob[]>([])
  const [reportsLoading, setReportsLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [reportSearch, setReportSearch] = useState('')
  const [reportDateRange, setReportDateRange] = useState<(Date | null)[] | null>(null)

  // New Summary Report dialog: pick a timeframe + which published reports to consolidate.
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryCandidates, setSummaryCandidates] = useState<ReportJob[]>([])
  const [summarySelectedIds, setSummarySelectedIds] = useState<Set<string>>(new Set())
  const [summaryRange, setSummaryRange] = useState<(Date | null)[]>(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - 7)
    return [start, end]
  })

  // Recent Reports has two modes: the published reports, or runs that still need a report.
  const [reportsMode, setReportsMode] = useState<'reports' | 'needs'>('reports')
  const [needsScope, setNeedsScope] = useState<'mine' | 'team'>('mine')
  const [needsSort, setNeedsSort] = useState<{
    field: 'project' | 'run' | 'description' | 'submittedBy' | 'caseCount' | 'dateModified'
    dir: 'asc' | 'desc'
  }>({ field: 'dateModified', dir: 'desc' })
  const [reportsSort, setReportsSort] = useState<{
    field: 'title' | 'runs' | 'author' | 'created' | 'modified' | 'status'
    dir: 'asc' | 'desc'
  }>({ field: 'modified', dir: 'desc' })
  const [{ data: runsOverviewData, loading: runsLoading }, getRunsOverview] = useGetRunsOverview()

  // Media picker dialog state (targets a specific section).
  const [pickerSectionId, setPickerSectionId] = useState<string | null>(null)
  const [pickerCaseId, setPickerCaseId] = useState<string | null>(null)
  const [pickerImages, setPickerImages] = useState<PostProcImage[]>([])
  const [pickerGifs, setPickerGifs] = useState<GifJob[]>([])
  const [pickerLoading, setPickerLoading] = useState(false)
  // Image search/filter state, mirroring the Headline Data gallery.
  const [pickerImageType, setPickerImageType] = useState<string | null>(null)
  const [pickerSectionFilter, setPickerSectionFilter] = useState<string | null>(null)
  const [pickerLocation, setPickerLocation] = useState<string | null>(null)
  const [pickerMetricTypes, setPickerMetricTypes] = useState<string[]>([])
  const [pickerFilters, setPickerFilters] = useState<PostProcImageFilters | null>(null)
  // Local-file upload from the media picker.
  const [uploadingMedia, setUploadingMedia] = useState(false)
  const uploadInputRef = useRef<HTMLInputElement>(null)

  // Markup modal state.
  const [showMarkup, setShowMarkup] = useState(false)
  const [markupSaving, setMarkupSaving] = useState(false)

  useEffect(() => {
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current)
    }
  }, [])

  // Land on the Recent Reports list.
  useEffect(() => {
    void loadReports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Load the Project/Run list once for the in-tab selector.
  useEffect(() => {
    let cancelled = false
    getProjectRun({ userId: '', page: 0, pageSize: 1000 } as any)
      .then((res: any) => {
        if (cancelled) return
        const rows = (res?.data?.result?.data_array || []) as any[][]
        const pairs = rows
          .map(r => ({ project: String(r[0] ?? ''), run: String(r[1] ?? '') }))
          .filter(p => p.project && p.run)
        // Dedupe project/run pairs.
        const seen = new Set<string>()
        setProjectRunPairs(
          pairs.filter(p => {
            const key = `${p.project}::${p.run}`
            if (seen.has(key)) return false
            seen.add(key)
            return true
          })
        )
      })
      .catch(() => {
        /* selector stays empty; right-click handoff still works */
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- Run selection -----------------------------------------------------
  const applyRunSelection = (project: string, runName: string, runCases: ReportRunCase[]) => {
    setSelectedProject(project || null)
    setSelectedRun(runName || null)
    setRunInfo({ project, runName })
    setCases(runCases.map(c => runCaseToReportCase(project, runName, c)))
    setTitle(prev => prev || [project, runName].filter(Boolean).join(' — '))
    void loadGeometrySection(runName)
  }

  // Pull the run's geometry-change images and surface them as a report section.
  const loadGeometrySection = async (runName: string) => {
    if (!runName) return
    try {
      const images = await getRunGeometryImages(runName)
      setSections(prev => {
        const withoutGeo = prev.filter(s => s.id !== GEOMETRY_SECTION_ID)
        if (!images.length) return withoutGeo.length ? withoutGeo : [newSection()]
        const geoSection: EditSection = {
          id: GEOMETRY_SECTION_ID,
          heading: 'Geometry Changes',
          commentsText: '',
          recommendation: '',
          images: images.map(img => ({
            url: img.url,
            caption: img.name,
            geomBlobPath: img.blobPath,
            geomGmid: img.gmid
          })),
          gifs: []
        }
        return [geoSection, ...withoutGeo]
      })
    } catch {
      // Geometry images are optional; ignore failures.
    }
  }

  // Append another run's cases (and geometry) to the report currently being edited.
  const addRunToReport = (project: string, runName: string, runCases: ReportRunCase[]) => {
    setCases(prev => {
      const existing = new Set(prev.map(c => c.gmId))
      const additions = runCases
        .map(c => runCaseToReportCase(project, runName, c))
        .filter(c => !existing.has(c.gmId))
      return [...prev, ...additions]
    })
    void appendGeometrySection(runName)
    toast.success(`Added ${runName} to this report`)
  }

  // Merge a run's geometry-change images into the shared Geometry Changes section.
  const appendGeometrySection = async (runName: string) => {
    if (!runName) return
    try {
      const images = await getRunGeometryImages(runName)
      if (!images.length) return
      const newImgs = images.map(img => ({
        url: img.url,
        caption: img.name,
        geomBlobPath: img.blobPath,
        geomGmid: img.gmid
      }))
      setSections(prev => {
        const geo = prev.find(s => s.id === GEOMETRY_SECTION_ID)
        if (!geo) {
          const geoSection: EditSection = {
            id: GEOMETRY_SECTION_ID,
            heading: 'Geometry Changes',
            commentsText: '',
            recommendation: '',
            images: newImgs,
            gifs: []
          }
          return [geoSection, ...prev]
        }
        const seen = new Set(geo.images.map(m => m.url))
        const merged = [...geo.images, ...newImgs.filter(m => !seen.has(m.url))]
        return prev.map(s => (s.id === GEOMETRY_SECTION_ID ? { ...s, images: merged } : s))
      })
    } catch {
      // Geometry images are optional; ignore failures.
    }
  }

  // Find the run's existing report (if any) so we don't create duplicates.
  const findExistingReportForRun = async (
    project: string,
    runName: string
  ): Promise<ReportJob | null> => {
    if (!runName) return null
    try {
      const all = await listReports(100)
      const matches = all.filter(r =>
        (r.payload?.cases ?? []).some(
          c => c.runName === runName && (!project || !c.projectName || c.projectName === project)
        )
      )
      if (!matches.length) return null
      // Prefer the most recently updated report for the run.
      return matches.reduce((a, b) => (b.timeUpdated > a.timeUpdated ? b : a))
    } catch {
      return null
    }
  }

  // Load a run handed off from Headline Data.
  useEffect(() => {
    if (!runHandoff) return
    const handoff = runHandoff
    setRunHandoff(null)
    if (!handoff.cases?.length) {
      toast.error('No cases found for the selected run')
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return
    }
    // A handoff always means "work on this report now" — show the editor, not the list.
    setView('editor')
    // Preload run media (from the Run Media tab) as a section.
    const applyRunMediaSection = () => {
      if (handoff.media?.length) {
        const media = handoff.media
        setSections(prev => {
          const withoutRunMedia = prev.filter(s => s.id !== 'run-media')
          const section: EditSection = {
            id: 'run-media',
            heading: 'Run Media',
            commentsText: '',
            recommendation: '',
            images: media.map(m => ({ url: m.url, caption: m.caption, type: m.type })),
            gifs: []
          }
          return [section, ...withoutRunMedia]
        })
      }
      // Marked-up images get their own "Mark-Up" section.
      if (handoff.markupMedia?.length) {
        const markup = handoff.markupMedia
        setSections(prev => {
          const withoutMarkup = prev.filter(s => s.id !== MARKUP_SECTION_ID)
          const section: EditSection = {
            id: MARKUP_SECTION_ID,
            heading: 'Mark-Up',
            commentsText: '',
            recommendation: '',
            images: markup.map(m => ({ url: m.url, caption: m.caption, type: m.type })),
            gifs: []
          }
          return [section, ...withoutMarkup]
        })
      }
    }
    void (async () => {
      // A run has a single report — open the existing one instead of duplicating it.
      const existing = await findExistingReportForRun(handoff.project, handoff.runName)
      if (existing) {
        openReportForEditing(existing)
        applyRunMediaSection()
        toast.success(`Opened the existing report for ${handoff.runName}`)
      } else {
        applyRunSelection(handoff.project, handoff.runName, handoff.cases)
        applyRunMediaSection()
        toast.success(`Started a new report for ${handoff.runName} (${handoff.cases.length} cases)`)
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runHandoff?.token])

  const projectOptions = useMemo(() => {
    const set = new Set(projectRunPairs.map(p => p.project))
    return Array.from(set)
      .sort()
      .map(p => ({ label: p, value: p }))
  }, [projectRunPairs])

  const runOptions = useMemo(() => {
    if (!selectedProject) return []
    return projectRunPairs
      .filter(p => p.project === selectedProject)
      .map(p => ({ label: p.run, value: p.run }))
  }, [projectRunPairs, selectedProject])

  // Fetch all cases of a run (with deltas) from Headline Data.
  const fetchRunCases = async (project: string, run: string): Promise<ReportRunCase[]> => {
    const res: any = await getHeadlineData({
      page: 0,
      pageSize: 10000,
      projects: project
    } as any)
    const allRows = parseHeadlineRows(res?.data)
    // Index the whole project by gm_id so references resolve for the delta math.
    const byGm = new Map<string, Record<string, any>>()
    allRows.forEach(r => {
      if (r.gm_id != null) byGm.set(String(r.gm_id), r)
    })
    // Delta = case coefficient − reference coefficient (Headline Data's definition).
    const deltaPairs: [keyof ReportRunCase, string][] = [
      ['delta_cd', 'cd'],
      ['delta_cl', 'cl'],
      ['delta_clf', 'clf'],
      ['delta_clr', 'clr'],
      ['delta_csf', 'csf'],
      ['delta_csr', 'csr'],
      ['delta_pct_clf', 'pct_clf']
    ]
    const rows = allRows.filter(r => String(r.run) === run)
    // Dedupe by state (last occurrence wins — matches active-case rule).
    const byState = new Map<string, ReportRunCase>()
    rows.forEach(r => {
      const gmId = String(r.gm_id ?? '')
      if (!gmId) return
      const ref = r.ref_gm_id != null ? byGm.get(String(r.ref_gm_id)) : undefined
      const entry: ReportRunCase = {
        gmId,
        systemRunId: `0-${gmId}`,
        stateName: r.state_name ? String(r.state_name) : undefined,
        refGmId: r.ref_gm_id != null ? String(r.ref_gm_id) : undefined,
        yaw: toNum(r.yaw),
        steer: toNum(r.steer),
        roll: toNum(r.roll),
        speed: toNum(r.speed)
      }
      deltaPairs.forEach(([deltaKey, coeffKey]) => {
        const a = toNum(r[coeffKey])
        const b = ref ? toNum(ref[coeffKey]) : undefined
        ;(entry as any)[deltaKey] = a != null && b != null ? a - b : toNum(r[deltaKey as string])
      })
      byState.set(String(r.state_name ?? gmId), entry)
    })
    return Array.from(byState.values())
  }

  // Load the selected run into the current report (seed if empty, else append).
  const loadRun = async () => {
    if (!selectedProject || !selectedRun) return
    setLoadingRun(true)
    try {
      const runCases = await fetchRunCases(selectedProject, selectedRun)
      if (!runCases.length) {
        toast.error('No cases found for that run')
        return
      }
      // Reports can span multiple runs: seed an empty editor, otherwise append this
      // run's cases (and geometry) to whatever is already loaded.
      if (!cases.length) {
        applyRunSelection(selectedProject, selectedRun, runCases)
      } else {
        addRunToReport(selectedProject, selectedRun, runCases)
      }
    } catch {
      toast.error('Failed to load run')
    } finally {
      setLoadingRun(false)
    }
  }

  // Start a brand-new report for a run (from the "needs a report" list).
  const startReportForRun = async (project: string, run: string) => {
    setLoadingRun(true)
    try {
      const runCases = await fetchRunCases(project, run)
      if (!runCases.length) {
        toast.error('No cases found for that run')
        return
      }
      resetEditor()
      applyRunSelection(project, run, runCases)
      setView('editor')
      toast.success(`Started a report for ${run}`)
    } catch {
      toast.error('Failed to load run')
    } finally {
      setLoadingRun(false)
    }
  }

  const removeCase = (gmId: string) => setCases(prev => prev.filter(c => c.gmId !== gmId))

  const toggleHighlight = (gmId: string) =>
    setCases(prev => prev.map(c => (c.gmId === gmId ? { ...c, highlighted: !c.highlighted } : c)))

  // --- Sections ----------------------------------------------------------
  const updateSection = (id: string, patch: Partial<EditSection>) =>
    setSections(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)))

  const removeSection = (id: string) => setSections(prev => prev.filter(s => s.id !== id))

  const removeMedia = (sectionId: string, kind: 'images' | 'gifs', url: string) =>
    setSections(prev =>
      prev.map(s => (s.id === sectionId ? { ...s, [kind]: s[kind].filter(m => m.url !== url) } : s))
    )

  // Drag-and-drop of media tiles between sections.
  const dragItemRef = useRef<{ sectionId: string; kind: 'images' | 'gifs'; url: string } | null>(
    null
  )
  const [dragOverSectionId, setDragOverSectionId] = useState<string | null>(null)

  const moveMedia = (
    from: { sectionId: string; kind: 'images' | 'gifs'; url: string },
    toSectionId: string
  ) => {
    if (from.sectionId === toSectionId) return
    setSections(prev => {
      const item = prev
        .find(s => s.id === from.sectionId)
        ?.[from.kind].find(m => m.url === from.url)
      if (!item) return prev
      return prev.map(s => {
        if (s.id === from.sectionId) {
          return { ...s, [from.kind]: s[from.kind].filter(m => m.url !== from.url) }
        }
        if (s.id === toSectionId) {
          if (s[from.kind].some(m => m.url === from.url)) return s
          return { ...s, [from.kind]: [...s[from.kind], item] }
        }
        return s
      })
    })
  }

  // --- Media picker ------------------------------------------------------
  const resetPickerFilters = () => {
    setPickerImageType(null)
    setPickerSectionFilter(null)
    setPickerLocation(null)
    setPickerMetricTypes([])
  }

  const openPicker = (sectionId: string) => {
    setPickerSectionId(sectionId)
    setPickerCaseId(cases[0]?.gmId ?? null)
    setPickerImages([])
    setPickerFilters(null)
    resetPickerFilters()
    loadGifs()
  }

  const loadGifs = async () => {
    try {
      const jobs = await listGifs(50)
      setPickerGifs(jobs.filter(g => g.status === 'completed' && g.resultUrl))
    } catch {
      // Non-fatal for the picker.
    }
  }

  // Seed the full set of filter options once per case (unfiltered request).
  useEffect(() => {
    if (!pickerSectionId || !pickerCaseId) return
    const found = cases.find(c => c.gmId === pickerCaseId)
    if (!found) return
    let cancelled = false
    fetchCaseImages(found.systemRunId, {}, 1)
      .then(res => !cancelled && setPickerFilters(res.availableFilters ?? null))
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [pickerSectionId, pickerCaseId, cases])

  useEffect(() => {
    if (!pickerSectionId || !pickerCaseId) return
    const found = cases.find(c => c.gmId === pickerCaseId)
    if (!found) return
    let cancelled = false
    setPickerLoading(true)
    fetchCaseImages(found.systemRunId, {
      imageType: pickerImageType,
      section: pickerSectionFilter,
      location: pickerLocation,
      metricTypes: pickerMetricTypes
    })
      .then(res => !cancelled && setPickerImages(res.images ?? []))
      .catch(() => !cancelled && toast.error('Failed to load images'))
      .finally(() => !cancelled && setPickerLoading(false))
    return () => {
      cancelled = true
    }
  }, [
    pickerSectionId,
    pickerCaseId,
    cases,
    pickerImageType,
    pickerSectionFilter,
    pickerLocation,
    pickerMetricTypes
  ])

  const attachImage = (img: PostProcImage) => {
    if (!pickerSectionId) return
    updateSectionMedia(pickerSectionId, 'images', { url: img.url, caption: img.name })
  }

  const attachGif = (gif: GifJob) => {
    if (!pickerSectionId || !gif.resultUrl) return
    updateSectionMedia(pickerSectionId, 'gifs', { url: gif.resultUrl, caption: gif.name })
  }

  const updateSectionMedia = (sectionId: string, kind: 'images' | 'gifs', ref: ReportMediaRef) =>
    setSections(prev =>
      prev.map(s => {
        if (s.id !== sectionId) return s
        if (s[kind].some(m => m.url === ref.url)) return s
        return { ...s, [kind]: [...s[kind], ref] }
      })
    )

  // Upload user-picked local files and attach them to the active section.
  const handleUploadFiles = async (fileList: FileList | null) => {
    if (!pickerSectionId || !fileList?.length) return
    setUploadingMedia(true)
    try {
      const refs = await uploadSectionMedia(Array.from(fileList))
      refs.forEach(ref => updateSectionMedia(pickerSectionId, 'images', ref))
      toast.success(`Added ${refs.length} file${refs.length === 1 ? '' : 's'}`)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Upload failed')
    } finally {
      setUploadingMedia(false)
      if (uploadInputRef.current) uploadInputRef.current.value = ''
    }
  }

  // Images already in the report that the user can open in the Markup modal.
  const markupImages = useMemo<MarkupImage[]>(() => {
    const out: MarkupImage[] = []
    const seen = new Set<string>()
    const add = (url?: string, name?: string) => {
      if (!url || seen.has(url)) return
      seen.add(url)
      out.push({ url, name: name || 'image' })
    }
    sections.forEach(s => s.images.forEach(m => add(m.url, m.caption)))
    mavMedia.forEach(m => m.type !== 'video' && add(m.url, m.caption))
    return out
  }, [sections, mavMedia])

  // Add the marked-up image to the "Mark-Up" section (creating it if needed).
  const addToMarkupSection = (ref: ReportMediaRef) =>
    setSections(prev => {
      const existing = prev.find(s => s.id === MARKUP_SECTION_ID)
      if (existing) {
        if (existing.images.some(m => m.url === ref.url)) return prev
        return prev.map(s =>
          s.id === MARKUP_SECTION_ID ? { ...s, images: [...s.images, ref] } : s
        )
      }
      const section: EditSection = {
        id: MARKUP_SECTION_ID,
        heading: 'Mark-Up',
        commentsText: '',
        recommendation: '',
        images: [ref],
        gifs: []
      }
      return [section, ...prev]
    })

  // Upload a marked-up image: add it to the Mark-Up section and to Run Media.
  const handleMarkupSave = async (file: File, caption: string) => {
    setMarkupSaving(true)
    try {
      const refs = await uploadSectionMedia([file])
      const uploaded = refs[0]
      if (!uploaded) throw new Error('Upload returned no file')
      const ref: ReportMediaRef = { url: uploaded.url, caption: caption || uploaded.caption }
      addToMarkupSection(ref)
      // Also persist to the run's media collection so it lives in Run Media too.
      if (runInfo?.runName) {
        try {
          await addRunMedia(
            runInfo.runName,
            [
              {
                url: uploaded.url,
                caption: caption || uploaded.caption,
                type: 'image',
                source: 'markup'
              }
            ],
            runInfo.project || undefined
          )
        } catch {
          toast.error('Saved to the report, but adding to Run Media failed')
        }
      }
      toast.success('Marked-up image added')
      setShowMarkup(false)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to save markup')
    } finally {
      setMarkupSaving(false)
    }
  }

  // --- Generate ----------------------------------------------------------
  const buildPayload = (): ReportPayload => ({
    title: title.trim(),
    subtitle: subtitle.trim() || undefined,
    author: author.trim() || undefined,
    mavMedia: mavMedia.length
      ? mavMedia
          .filter(m => m.url)
          .map(m => ({ url: m.url as string, caption: m.caption || m.name, type: m.type }))
      : undefined,
    mavNotes: mavNotes.length ? mavNotes : undefined,
    summaryReportNames: summaryReportNames.length ? summaryReportNames : undefined,
    cases: cases.map(c => ({
      caseName: c.stateName || c.gmId,
      gm_id: c.gmId,
      systemRunId: c.systemRunId,
      projectName: c.projectName,
      runName: c.runName,
      yaw: c.yaw,
      steer: c.steer,
      roll: c.roll,
      speed: c.speed
    })),
    deltaMetrics: cases.map(c => ({
      ...c.delta,
      caseName: c.stateName || c.gmId,
      highlight: c.highlighted
    })),
    sections: sections
      .filter(
        s =>
          s.heading.trim() ||
          s.commentsText.trim() ||
          s.images.length ||
          s.gifs.length ||
          s.deltaMetrics?.length
      )
      .map<ReportSectionInput>(s => ({
        heading: s.heading.trim() || 'Section',
        comments: s.commentsText
          .split('\n')
          .map(l => l.trim())
          .filter(Boolean),
        recommendation: s.recommendation.trim() || undefined,
        images: s.images,
        gifs: s.gifs,
        anchorId: s.anchorId,
        deltaMetrics: s.deltaMetrics
      }))
  })

  const poll = (reportId: string) => {
    pollRef.current = setTimeout(async () => {
      try {
        const updated = await getReportJob(reportId)
        setJob(updated)
        if (updated.status === 'completed') {
          setGenerating(false)
          toast.success('Report generated')
        } else if (updated.status === 'failed') {
          setGenerating(false)
          toast.error(updated.error || 'Report generation failed')
        } else {
          poll(reportId)
        }
      } catch {
        setGenerating(false)
        toast.error('Lost track of the report job')
      }
    }, POLL_INTERVAL_MS)
  }

  const handleGenerate = async () => {
    if (!title.trim()) {
      toast.error('A report title is required')
      return
    }
    setGenerating(true)
    setJob(null)
    try {
      const created = await createReport(buildPayload())
      setJob(created)
      setEditingReportId(created.reportId)
      poll(created.reportId)
    } catch (err: any) {
      setGenerating(false)
      toast.error(err?.response?.data?.error || 'Failed to start report generation')
    }
  }

  // Save changes to the report currently being edited, then rebuild it.
  const handleSave = async () => {
    if (!editingReportId) return
    if (!title.trim()) {
      toast.error('A report title is required')
      return
    }
    setSaving(true)
    try {
      const updated = await updateReport(editingReportId, buildPayload())
      setJob(updated)
      setSaving(false)
      setGenerating(true)
      poll(updated.reportId)
      toast.success('Report saved')
    } catch (err: any) {
      setSaving(false)
      toast.error(err?.response?.data?.error || 'Failed to save report')
    }
  }

  // --- Recent Reports ----------------------------------------------------
  const loadReports = async () => {
    setReportsLoading(true)
    try {
      setReports(await listReports(100))
    } catch {
      toast.error('Failed to load reports')
    } finally {
      setReportsLoading(false)
    }
  }

  const openReportsList = () => {
    setView('list')
    void loadReports()
  }

  const resetEditor = () => {
    setEditingReportId(null)
    setJob(null)
    setTitle('')
    setSubtitle('')
    setAuthor([currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' '))
    setCases([])
    setMavMedia([])
    setMavNotes([])
    setSummaryReportNames([])
    setRunInfo(null)
    setSelectedProject(null)
    setSelectedRun(null)
    setSections([newSection()])
  }

  const newReport = () => {
    resetEditor()
    setView('editor')
  }

  // --- New Summary Report ------------------------------------------------
  const openSummaryModal = async () => {
    setShowSummaryModal(true)
    setSummaryLoading(true)
    try {
      const all = await listReports(100)
      // Only published reports can be consolidated, and never a summary of summaries.
      setSummaryCandidates(
        all.filter(r => r.status === 'completed' && !r.payload?.summaryReportNames?.length)
      )
    } catch {
      toast.error('Failed to load reports')
    } finally {
      setSummaryLoading(false)
    }
  }

  const summaryInRange = useMemo(() => {
    const [start, end] = summaryRange
    const startMs = start ? new Date(start).setHours(0, 0, 0, 0) : -Infinity
    const endMs = end ? new Date(end).setHours(23, 59, 59, 999) : Infinity
    return summaryCandidates
      .filter(r => r.timeUpdated >= startMs && r.timeUpdated <= endMs)
      .sort((a, b) => a.timeUpdated - b.timeUpdated)
  }, [summaryCandidates, summaryRange])

  // Default every report in range to selected whenever the range or candidates change.
  useEffect(() => {
    if (!showSummaryModal) return
    setSummarySelectedIds(new Set(summaryInRange.map(r => r.reportId)))
  }, [showSummaryModal, summaryInRange])

  const toggleSummaryReport = (reportId: string) =>
    setSummarySelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(reportId)) next.delete(reportId)
      else next.add(reportId)
      return next
    })

  const createSummary = () => {
    const selected = summaryInRange.filter(r => summarySelectedIds.has(r.reportId))
    if (!selected.length) {
      toast.error('Select at least one report to include')
      return
    }
    const [start, end] = summaryRange
    const fmtShort = (d: Date | null) => (d ? new Date(d).toLocaleDateString('en-US') : '')
    const dateLabel = [fmtShort(start), fmtShort(end)].filter(Boolean).join(' – ')

    resetEditor()
    setTitle(dateLabel ? `CFD Weekly Summary (${dateLabel})` : 'CFD Weekly Summary')
    setSummaryReportNames(selected.map(r => r.payload?.title || r.title))

    // Lay the reports out consecutively like a slide deck: each report is a titled
    // divider (the anchor the "Reports Included" list scrolls to) followed by that
    // report's own sections, in order — not merged together.
    const deck: EditSection[] = []
    const rid = (idx: number, sIdx: number) =>
      `${Date.now()}-${idx}-${sIdx}-${Math.random().toString(36).slice(2, 7)}`
    selected.forEach((r, idx) => {
      const p = r.payload || ({} as ReportPayload)
      const rTitle = p.title || r.title
      deck.push({
        id: rid(idx, -1),
        heading: rTitle,
        commentsText: '',
        recommendation: '',
        images: [],
        gifs: [],
        anchorId: `report-${idx}`,
        deltaMetrics: p.deltaMetrics
      })
      ;(p.sections ?? []).forEach((s, sIdx) => {
        deck.push({
          id: rid(idx, sIdx),
          heading: s.heading || '',
          commentsText: (s.comments ?? []).join('\n'),
          recommendation: s.recommendation || '',
          images: s.images ?? [],
          gifs: s.gifs ?? []
        })
      })
    })
    setSections(deck.length ? deck : [newSection()])
    // Each report keeps its own content; no merged delta/cases table.
    setCases([])

    setShowSummaryModal(false)
    setView('editor')
    toast.success(
      `Summary created from ${selected.length} report${selected.length === 1 ? '' : 's'}`
    )
  }

  // Rebuild the editor form from a saved report so it can be edited & re-saved.
  const openReportForEditing = (report: ReportJob) => {
    const p = report.payload || ({} as ReportPayload)
    setEditingReportId(report.reportId)
    setJob(report)
    setTitle(p.title || '')
    setSubtitle(p.subtitle || '')
    setAuthor(p.author || '')
    setMavMedia(report.mavMedia ?? [])
    setMavNotes(report.mavNotes ?? p.mavNotes ?? [])
    setSummaryReportNames(p.summaryReportNames ?? [])
    const rebuilt = casesFromPayload(p)
    setCases(rebuilt)
    const first = (p.cases ?? [])[0]
    setRunInfo(first ? { project: first.projectName ?? '', runName: first.runName ?? '' } : null)
    setSections(
      (p.sections ?? []).length
        ? (p.sections ?? []).map(s => ({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            heading: s.heading || '',
            commentsText: (s.comments ?? []).join('\n'),
            recommendation: s.recommendation || '',
            images: s.images ?? [],
            gifs: s.gifs ?? [],
            anchorId: s.anchorId,
            deltaMetrics: s.deltaMetrics
          }))
        : [newSection()]
    )
    setGenerating(false)
    setView('editor')
  }

  const handleDeleteReport = async (report: ReportJob) => {
    if (!window.confirm(`Delete report "${report.title}"? This cannot be undone.`)) return
    try {
      await deleteReport(report.reportId)
      setReports(prev => prev.filter(r => r.reportId !== report.reportId))
      if (editingReportId === report.reportId) resetEditor()
      toast.success('Report deleted')
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to delete report')
    }
  }

  const handlePreview = async () => {
    if (!title.trim()) {
      toast.error('A report title is required')
      return
    }
    setPreviewing(true)
    try {
      const html = await previewReport(buildPayload())
      // Open the built HTML in a new tab. Media still uses the source URLs here.
      const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }))
      window.open(blobUrl, '_blank', 'noreferrer')
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to build preview')
    } finally {
      setPreviewing(false)
    }
  }

  // Download a self-contained .html file (media embedded as base64).
  const handleDownload = async () => {
    if (!title.trim()) {
      toast.error('A report title is required')
      return
    }
    setDownloading(true)
    try {
      const html = await previewReport(buildPayload(), true)
      const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }))
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `${title.trim().replace(/[^a-z0-9-_]+/gi, '_') || 'report'}.html`
      link.click()
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to build download')
    } finally {
      setDownloading(false)
    }
  }

  // Download a PowerPoint (.pptx) built from the report payload.
  const handleExportPptx = async () => {
    if (!title.trim()) {
      toast.error('A report title is required')
      return
    }
    setExportingPptx(true)
    try {
      const blob = await exportReportPptx(buildPayload())
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `${title.trim().replace(/[^a-z0-9-_]+/gi, '_') || 'report'}.pptx`
      link.click()
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to build PowerPoint')
    } finally {
      setExportingPptx(false)
    }
  }

  const copyLink = () => {
    if (!job?.resultUrl) return
    navigator.clipboard.writeText(job.resultUrl)
    toast.success('Link copied')
  }

  const caseOptions = useMemo(
    () =>
      cases.map(c => ({
        label: c.stateName ? `${c.gmId} · ${c.stateName}` : c.gmId,
        value: c.gmId
      })),
    [cases]
  )
  const pickerSection = sections.find(s => s.id === pickerSectionId)

  // Filter dropdown options, seeded from the case's availableFilters.
  const pickerImageTypeOptions = useMemo(() => {
    const types = pickerFilters?.imageTypes ?? []
    return [
      { label: 'All Types', value: null },
      ...types.map(t => ({ label: t === 'Sect' ? 'Section' : t, value: t }))
    ]
  }, [pickerFilters?.imageTypes])
  const pickerSectionOptions = useMemo(() => {
    const list = pickerFilters?.sections ?? []
    return [{ label: 'All Sect.', value: null }, ...list.map(s => ({ label: s, value: s }))]
  }, [pickerFilters?.sections])
  const pickerLocationOptions = useMemo(() => {
    const list = pickerFilters?.locations ?? []
    return [{ label: 'All Loc.', value: null }, ...list.map(l => ({ label: l, value: l }))]
  }, [pickerFilters?.locations])
  const pickerMetricTypeOptions = useMemo(() => {
    const types = pickerFilters?.metricTypes ?? []
    const referenceMetrics = pickerFilters?.referenceMetrics ?? []
    const compareOptions = referenceMetrics.map(rm => ({
      label: `${rm.metricType} (${rm.referenceRunId})`,
      value: `${rm.metricType}||${rm.referenceRunId}`
    }))
    return [...types.map(t => ({ label: t, value: t })), ...compareOptions]
  }, [pickerFilters?.metricTypes, pickerFilters?.referenceMetrics])

  // Only show GIFs whose frames came from a case in the loaded run.
  const runSystemRunIds = useMemo(() => new Set(cases.map(c => c.systemRunId)), [cases])
  const visibleGifs = useMemo(
    () =>
      pickerGifs.filter(g =>
        (g.frames ?? []).some(fr => fr.caseId && runSystemRunIds.has(fr.caseId))
      ),
    [pickerGifs, runSystemRunIds]
  )

  // Recent Reports filtered by title search and Last-Modified date range.
  const filteredReports = useMemo(() => {
    const q = reportSearch.trim().toLowerCase()
    const [start, end] = reportDateRange ?? []
    const startMs = start ? new Date(start).setHours(0, 0, 0, 0) : null
    const endMs = end ? new Date(end).setHours(23, 59, 59, 999) : null
    return reports.filter(r => {
      if (q && !(r.title || '').toLowerCase().includes(q)) return false
      const modified = r.timeUpdated ?? r.timeCreated ?? 0
      if (startMs != null && modified < startMs) return false
      if (endMs != null && modified > endMs) return false
      return true
    })
  }, [reports, reportSearch, reportDateRange])

  // Client-side sort for the Recent Reports table.
  const sortedReports = useMemo(() => {
    const { field, dir } = reportsSort
    const sign = dir === 'asc' ? 1 : -1
    const valueOf = (r: ReportJob): string | number => {
      switch (field) {
        case 'title':
          return (r.title || '').toLowerCase()
        case 'runs':
          return Array.from(new Set((r.payload?.cases ?? []).map(c => c.runName).filter(Boolean)))
            .join(', ')
            .toLowerCase()
        case 'author':
          return (r.payload?.author || r.createByUserName || '').toLowerCase()
        case 'created':
          return r.timeCreated ?? 0
        case 'status':
          return (r.status || '').toLowerCase()
        case 'modified':
        default:
          return r.timeUpdated ?? r.timeCreated ?? 0
      }
    }
    return [...filteredReports].sort((a, b) => {
      const av = valueOf(a)
      const bv = valueOf(b)
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), undefined, { numeric: true })
      return cmp * sign
    })
  }, [filteredReports, reportsSort])

  const toggleReportsSort = (field: typeof reportsSort.field) =>
    setReportsSort(prev =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: field === 'created' || field === 'modified' ? 'desc' : 'asc' }
    )

  // Every project|run pair already covered by a report (draft or published).
  const coveredRunKeys = useMemo(() => {
    const keys = new Set<string>()
    reports.forEach(r =>
      (r.payload?.cases ?? []).forEach(c => {
        if (c.runName) {
          keys.add(`${(c.projectName ?? '').toLowerCase()}|${c.runName.toLowerCase()}`)
        }
      })
    )
    return keys
  }, [reports])

  const runsOverview = useMemo(() => {
    const rows = ((runsOverviewData as any)?.runs ?? []) as any[]
    return rows.map(r => {
      // Prefer endTime (the run's "Completed Date"); fall back to dateModified. Databricks
      // timestamps may be space-separated ("2026-10-02 17:34:56") or epoch numbers.
      const raw = r.endTime ?? r.dateModified
      let ms = 0
      if (raw != null && raw !== '') {
        ms =
          typeof raw === 'number' || /^\d+$/.test(String(raw))
            ? Number(raw)
            : new Date(String(raw).replace(' ', 'T')).getTime()
      }
      return {
        project: String(r.project ?? ''),
        run: String(r.run ?? ''),
        description: String(r.description ?? ''),
        submittedBy: String(r.submittedBy ?? ''),
        dateModified: Number.isNaN(ms) ? 0 : ms,
        caseCount: Number(r.caseCount ?? 0)
      }
    })
  }, [runsOverviewData])

  // Runs modified in the selected range that don't yet have a report.
  const runsNeedingReport = useMemo(() => {
    const q = reportSearch.trim().toLowerCase()
    const [start, end] = reportDateRange ?? []
    const startMs = start ? new Date(start).setHours(0, 0, 0, 0) : null
    const endMs = end ? new Date(end).setHours(23, 59, 59, 999) : null
    return runsOverview.filter(r => {
      if (!r.run) return false
      if (coveredRunKeys.has(`${r.project.toLowerCase()}|${r.run.toLowerCase()}`)) return false
      if (q && !`${r.project} ${r.run}`.toLowerCase().includes(q)) return false
      if (startMs != null && r.dateModified && r.dateModified < startMs) return false
      if (endMs != null && r.dateModified && r.dateModified > endMs) return false
      return true
    })
  }, [runsOverview, coveredRunKeys, reportSearch, reportDateRange])

  // Client-side sort for the "needs a report" table.
  const sortedNeeds = useMemo(() => {
    const { field, dir } = needsSort
    const sign = dir === 'asc' ? 1 : -1
    return [...runsNeedingReport].sort((a, b) => {
      const cmp =
        field === 'caseCount' || field === 'dateModified'
          ? (a[field] || 0) - (b[field] || 0)
          : String(a[field]).localeCompare(String(b[field]), undefined, { numeric: true })
      return cmp * sign
    })
  }, [runsNeedingReport, needsSort])

  const toggleNeedsSort = (field: typeof needsSort.field) =>
    setNeedsSort(prev =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: field === 'caseCount' || field === 'dateModified' ? 'desc' : 'asc' }
    )

  const loadNeeds = () => {
    void loadReports()
    void getRunsOverview({ userId: needsScope === 'mine' ? currentUser?._id : '' } as any)
  }

  // Load the "needs a report" data when that view is shown or its scope changes.
  useEffect(() => {
    if (reportsMode === 'needs') {
      // Default the range to the last 7 days the first time this view is opened.
      if (!reportDateRange) applyDatePreset(7)
      loadNeeds()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportsMode, needsScope])

  // Quick date-range presets shared by both Recent Reports views.
  const applyDatePreset = (days: number | null) => {
    if (days == null) {
      setReportDateRange(null)
      return
    }
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - days)
    setReportDateRange([start, end])
  }

  return (
    <div className="report-generator p-4 space-y-4">
      {/* Button icon/label spacing + readable text in dark mode (neutral-20/40 aren't in the theme). */}
      <style>{`
        .report-generator button[data-pc-name="button"] { gap: .5rem; }
        .report-generator button[data-pc-name="button"] .p-button-icon-left { margin-right: 0; }
        .report-generator button[data-pc-name="button"] .p-button-icon-right { margin-left: 0; }
        /* Shorten dropdowns so their height matches the buttons. */
        .report-generator [data-pc-name="dropdown"] [data-pc-section="input"] {
          padding-top: .5rem !important;
          padding-bottom: .5rem !important;
        }
        .dark .report-generator .p-inputtext,
        .dark .report-generator .p-inputtextarea,
        .dark .report-generator .p-dropdown .p-dropdown-label { color: #e5e7eb; }
        .dark .report-generator .p-inputtext::placeholder,
        .dark .report-generator .p-inputtextarea::placeholder { color: #9ca3af; }
        .dark .report-generator .text-gray-600 { color: #EFEFEF !important; }
        .dark .report-generator .text-gray-500 { color: #AAAAAA !important; }
      `}</style>

      {/* View switch */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          <Button
            label={editingReportId ? 'Editor' : 'New Report'}
            icon="pi pi-pencil"
            outlined={view !== 'editor'}
            onClick={() => setView('editor')}
          />
          <Button
            label="Recent Reports"
            icon="pi pi-history"
            outlined={view !== 'list'}
            onClick={openReportsList}
          />
          {userCanMutate && (
            <Button
              label="New Summary Report"
              icon="pi pi-calendar"
              outlined
              onClick={openSummaryModal}
              tooltip="Consolidate published reports from a timeframe into a weekly summary"
              tooltipOptions={{ position: 'top' }}
            />
          )}
        </div>
        {view === 'editor' && editingReportId && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-20">
            <i className="pi pi-pencil" />
            Editing saved report
            <Button label="Start New" icon="pi pi-plus" text size="small" onClick={newReport} />
          </div>
        )}
      </div>

      {view === 'list' ? (
        <div className="dark:bg-neutral-80 bg-white p-4 rounded-md border border-gray-200 dark:border-neutral-70">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex rounded-md overflow-hidden border border-gray-200 dark:border-neutral-70">
                <button
                  className={classNames(
                    'px-3 py-1.5 text-sm font-medium',
                    reportsMode === 'reports'
                      ? 'bg-gm-blue text-white'
                      : 'text-gray-600 dark:text-neutral-20'
                  )}
                  onClick={() => setReportsMode('reports')}>
                  Reports
                </button>
                <button
                  className={classNames(
                    'px-3 py-1.5 text-sm font-medium',
                    reportsMode === 'needs'
                      ? 'bg-gm-blue text-white'
                      : 'text-gray-600 dark:text-neutral-20'
                  )}
                  title="Runs modified in the date range that don't have a report yet"
                  onClick={() => setReportsMode('needs')}>
                  Needs a report
                </button>
              </div>
              {reportsMode === 'needs' && (
                <div className="flex rounded-md overflow-hidden border border-gray-200 dark:border-neutral-70">
                  <button
                    className={classNames(
                      'px-3 py-1.5 text-sm font-medium',
                      needsScope === 'mine'
                        ? 'bg-gm-blue text-white'
                        : 'text-gray-600 dark:text-neutral-20'
                    )}
                    onClick={() => setNeedsScope('mine')}>
                    Mine
                  </button>
                  <button
                    className={classNames(
                      'px-3 py-1.5 text-sm font-medium',
                      needsScope === 'team'
                        ? 'bg-gm-blue text-white'
                        : 'text-gray-600 dark:text-neutral-20'
                    )}
                    onClick={() => setNeedsScope('team')}>
                    Team
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <InputText
                  value={reportSearch}
                  onChange={e => setReportSearch(e.target.value)}
                  placeholder={reportsMode === 'needs' ? 'Search by run' : 'Search by title'}
                  className="w-52"
                />
              </span>
              <div className="flex items-center gap-1">
                <Button label="7 days" text size="small" onClick={() => applyDatePreset(7)} />
                <Button label="30 days" text size="small" onClick={() => applyDatePreset(30)} />
                <Button label="All" text size="small" onClick={() => applyDatePreset(null)} />
              </div>
              <Calendar
                value={reportDateRange as Date[] | null}
                onChange={e => setReportDateRange((e.value as (Date | null)[]) ?? null)}
                selectionMode="range"
                readOnlyInput
                showButtonBar
                placeholder="Modified date range"
                dateFormat="mm/dd/yy"
                className="w-60"
              />
              <Button
                icon="pi pi-refresh"
                outlined
                onClick={() => (reportsMode === 'needs' ? loadNeeds() : loadReports())}
                loading={reportsLoading || runsLoading}
                tooltip="Reload the list"
              />
            </div>
          </div>
          {reportsMode === 'reports' ? (
            reportsLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-20">
                <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" /> Loading…
              </div>
            ) : filteredReports.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-neutral-60">No reports found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b border-gray-200 dark:border-neutral-70 text-gray-600 dark:text-neutral-20">
                      {(
                        [
                          { field: 'title', label: 'Title' },
                          { field: 'runs', label: 'Run(s)' },
                          { field: 'author', label: 'Author' },
                          { field: 'created', label: 'Created' },
                          { field: 'modified', label: 'Last Modified' },
                          { field: 'status', label: 'Status' }
                        ] as const
                      ).map(col => (
                        <th key={col.field} className="py-2 pr-3">
                          <button
                            className="inline-flex items-center gap-1 font-semibold hover:text-gm-blue"
                            onClick={() => toggleReportsSort(col.field)}>
                            {col.label}
                            {reportsSort.field === col.field && (
                              <i
                                className={classNames(
                                  'pi text-[10px]',
                                  reportsSort.dir === 'asc' ? 'pi-sort-up' : 'pi-sort-down'
                                )}
                              />
                            )}
                          </button>
                        </th>
                      ))}
                      <th className="py-2 pr-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedReports.map(r => {
                      const owned = r.createByUser === currentUser?._id
                      const runNames = Array.from(
                        new Set(
                          (r.payload?.cases ?? [])
                            .map(c => c.runName)
                            .filter((n): n is string => !!n)
                        )
                      )
                      const summaryCount = r.payload?.summaryReportNames?.length ?? 0
                      return (
                        <tr
                          key={r.reportId}
                          className="border-b border-gray-100 dark:border-neutral-70">
                          <td className="py-2 pr-3 dark:text-neutral-10">
                            {summaryCount > 0 && (
                              <span className="mr-2 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-gm-blue/10 text-gm-blue">
                                Summary
                              </span>
                            )}
                            {r.title}
                          </td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-neutral-20">
                            {summaryCount > 0 ? (
                              <span title={(r.payload?.summaryReportNames ?? []).join(', ')}>
                                {summaryCount} report{summaryCount === 1 ? '' : 's'}
                              </span>
                            ) : runNames.length ? (
                              <span title={runNames.join(', ')}>
                                {runNames.length > 2
                                  ? `${runNames.slice(0, 2).join(', ')} +${runNames.length - 2}`
                                  : runNames.join(', ')}
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-neutral-20">
                            {r.payload?.author || r.createByUserName || '—'}
                          </td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-neutral-20">
                            {fmtDate(r.timeCreated)}
                          </td>
                          <td className="py-2 pr-3 text-gray-600 dark:text-neutral-20">
                            {fmtDate(r.timeUpdated)}
                          </td>
                          <td className="py-2 pr-3">
                            <span
                              className={classNames('text-xs font-semibold', {
                                'text-green-600': r.status === 'completed',
                                'text-red-600': r.status === 'failed',
                                'text-gray-500 dark:text-neutral-60':
                                  r.status !== 'completed' && r.status !== 'failed'
                              })}>
                              {r.status === 'completed' ? 'Published' : r.status}
                            </span>
                          </td>
                          <td className="py-2 pr-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                icon="pi pi-pencil"
                                label="Open"
                                size="small"
                                text
                                tooltip="Open this report for editing"
                                onClick={() => openReportForEditing(r)}
                              />
                              {r.status === 'completed' && r.resultUrl && (
                                <Button
                                  icon="pi pi-external-link"
                                  size="small"
                                  text
                                  tooltip="View the published report in a new tab"
                                  onClick={() => window.open(r.resultUrl, '_blank', 'noreferrer')}
                                />
                              )}
                              <Button
                                icon="pi pi-trash"
                                size="small"
                                text
                                severity="danger"
                                disabled={!owned}
                                tooltip={
                                  owned
                                    ? 'Delete this report permanently'
                                    : 'Only the creator can delete this report'
                                }
                                onClick={() => handleDeleteReport(r)}
                              />
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : runsLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-20">
              <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" /> Loading…
            </div>
          ) : runsNeedingReport.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-neutral-60">
              {needsScope === 'mine' ? 'None of your runs' : 'No team runs'} in this range are
              missing a report. 🎉
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-200 dark:border-neutral-70 text-gray-600 dark:text-neutral-20">
                    {(
                      [
                        { field: 'project', label: 'Project', align: 'left' },
                        { field: 'run', label: 'Run', align: 'left' },
                        { field: 'description', label: 'Description', align: 'left' },
                        { field: 'submittedBy', label: 'Submitted By', align: 'left' },
                        { field: 'dateModified', label: 'Completed', align: 'left' }
                      ] as const
                    ).map(col => (
                      <th key={col.field} className="py-2 pr-3">
                        <button
                          className="inline-flex items-center gap-1 font-semibold hover:text-gm-blue"
                          onClick={() => toggleNeedsSort(col.field)}>
                          {col.label}
                          {needsSort.field === col.field && (
                            <i
                              className={classNames(
                                'pi text-[10px]',
                                needsSort.dir === 'asc' ? 'pi-sort-up' : 'pi-sort-down'
                              )}
                            />
                          )}
                        </button>
                      </th>
                    ))}
                    <th className="py-2 pr-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedNeeds.map(r => (
                    <tr
                      key={`${r.project}|${r.run}`}
                      className="border-b border-gray-100 dark:border-neutral-70">
                      <td className="py-2 pr-3 text-gray-600 dark:text-neutral-20">{r.project}</td>
                      <td className="py-2 pr-3 dark:text-neutral-10">{r.run}</td>
                      <td
                        className="py-2 pr-3 text-gray-600 dark:text-neutral-20 max-w-xs truncate"
                        title={r.description}>
                        {r.description || '—'}
                      </td>
                      <td className="py-2 pr-3 text-gray-600 dark:text-neutral-20">
                        {r.submittedBy || '—'}
                      </td>
                      <td className="py-2 pr-3 text-gray-600 dark:text-neutral-20">
                        {r.dateModified ? fmtDate(r.dateModified) : '—'}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center justify-end">
                          <Button
                            icon="pi pi-plus"
                            label="Start Report"
                            size="small"
                            text
                            loading={loadingRun}
                            onClick={() => startReportForRun(r.project, r.run)}
                            tooltip="Open the Report Generator seeded with this run"
                            tooltipOptions={{ position: 'left' }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Reports Included — a summary report's source reports; click to jump to one. */}
          {summaryReportNames.length > 0 && (
            <div className="dark:bg-neutral-80 bg-white p-4 rounded-md border border-gray-200 dark:border-neutral-70">
              <h3 className="font-semibold mb-3 dark:text-neutral-10">
                Reports Included{' '}
                <span className="text-xs font-normal text-gray-500 dark:text-neutral-60">
                  · {summaryReportNames.length}
                </span>
              </h3>
              <div className="flex flex-wrap gap-2">
                {summaryReportNames.map((name, idx) => (
                  <button
                    key={`${idx}-${name}`}
                    type="button"
                    onClick={() =>
                      document
                        .getElementById(`report-${idx}`)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }
                    className="text-sm px-3 py-1 rounded-full border border-gm-blue/40 text-gm-blue hover:bg-gm-blue/10"
                    title="Scroll to this report">
                    {name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* MAV media (external, pushed by MAV — read-only) */}
          {(mavMedia.length > 0 || mavNotes.length > 0) && (
            <div className="dark:bg-neutral-80 bg-white p-4 rounded-md border border-gray-200 dark:border-neutral-70">
              <h3 className="font-semibold mb-3 dark:text-neutral-10">
                MAV{' '}
                <span className="text-xs font-normal text-gray-500 dark:text-neutral-60">
                  · {mavMedia.length} item{mavMedia.length === 1 ? '' : 's'} from MAV
                </span>
              </h3>
              {mavNotes.length > 0 && (
                <ul className="list-disc pl-5 mb-3 text-sm text-gray-700 dark:text-neutral-50 space-y-1">
                  {mavNotes.map((n, i) => (
                    <li key={i}>{n}</li>
                  ))}
                </ul>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {mavMedia.map(m => (
                  <figure
                    key={m.blobPath}
                    className="m-0 rounded overflow-hidden border border-gray-200 dark:border-neutral-70">
                    {m.type === 'video' ? (
                      <video
                        src={m.url}
                        controls
                        preload="metadata"
                        className="w-full h-28 object-cover bg-black"
                      />
                    ) : (
                      <img
                        src={m.url}
                        alt={m.caption || m.name}
                        className="w-full h-28 object-cover"
                      />
                    )}
                    <figcaption
                      className="px-2 py-1 text-xs text-gray-600 dark:text-neutral-20 truncate"
                      title={m.caption || m.name}>
                      {m.caption || m.name}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          )}
          {/* Report metadata */}
          <div className="dark:bg-neutral-80 bg-white p-4 rounded-md border border-gray-200 dark:border-neutral-70">
            <h3 className="font-semibold mb-3 dark:text-neutral-10">Report Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <span className="flex flex-col gap-1">
                <label className="text-sm text-gray-600 dark:text-neutral-20">Title *</label>
                <InputText
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="Flattened LE of Splitter"
                />
              </span>
              <span className="flex flex-col gap-1">
                <label className="text-sm text-gray-600 dark:text-neutral-20">Subtitle</label>
                <InputText
                  value={subtitle}
                  onChange={e => setSubtitle(e.target.value)}
                  placeholder="GM Request · 2026 C3 Package"
                />
              </span>
              <span className="flex flex-col gap-1">
                <label className="text-sm text-gray-600 dark:text-neutral-20">Author</label>
                <InputText
                  value={author}
                  onChange={e => setAuthor(e.target.value)}
                  placeholder="Alexis Gutierrez"
                />
              </span>
            </div>
          </div>

          {/* Run selection + delta metrics */}
          <div className="dark:bg-neutral-80 bg-white p-4 rounded-md border border-gray-200 dark:border-neutral-70">
            <div className="flex items-end justify-between mb-3 flex-wrap gap-3">
              <div>
                <h3 className="font-semibold dark:text-neutral-10">Run &amp; Delta Metrics</h3>
                {runInfo && (
                  <p className="text-sm text-gray-500 dark:text-neutral-60 mt-1">
                    {runInfo.project} · {runInfo.runName} — {cases.length} case
                    {cases.length === 1 ? '' : 's'}
                  </p>
                )}
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <span className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600 dark:text-neutral-20">Project</label>
                  <Dropdown
                    value={selectedProject}
                    options={projectOptions}
                    onChange={e => {
                      setSelectedProject(e.value)
                      setSelectedRun(null)
                    }}
                    filter
                    placeholder="Select project"
                    className="w-44 text-sm"
                    tooltip="Select a project to list its runs"
                    tooltipOptions={{ position: 'top' }}
                  />
                </span>
                <span className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600 dark:text-neutral-20">Run</label>
                  <Dropdown
                    value={selectedRun}
                    options={runOptions}
                    onChange={e => setSelectedRun(e.value)}
                    filter
                    disabled={!selectedProject}
                    placeholder="Select run"
                    className="w-44 text-sm"
                    tooltip={!selectedProject ? 'Select a project first' : 'Select a run to load'}
                    tooltipOptions={{ position: 'top' }}
                  />
                </span>
                <Button
                  label={cases.length ? 'Add Run' : 'Load Run'}
                  icon={cases.length ? 'pi pi-plus' : 'pi pi-download'}
                  onClick={loadRun}
                  loading={loadingRun}
                  disabled={!selectedProject || !selectedRun}
                  tooltip={
                    cases.length
                      ? "Append this run's cases and geometry to the report"
                      : "Load this run's cases into the report"
                  }
                  tooltipOptions={{ position: 'top' }}
                />
              </div>
            </div>

            {cases.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-neutral-60">
                Select a project and run and click <strong>Load Run</strong> — or right-click a run
                in Headline Data and choose <strong>Open Report</strong>. All cases in the run are
                included with their delta metrics. Once a run is loaded you can keep adding more
                runs with <strong>Add Run</strong>.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full table-fixed text-sm min-w-[820px]">
                  <colgroup>
                    <col style={{ width: '22%' }} />
                    <col style={{ width: '8%' }} />
                    <col style={{ width: '8%' }} />
                    {DELTA_FIELDS.map(f => (
                      <col key={f.key} style={{ width: '8%' }} />
                    ))}
                    <col style={{ width: '6%' }} />
                  </colgroup>
                  <thead>
                    <tr className="text-gray-500 dark:text-neutral-20 border-b border-gray-200 dark:border-neutral-70">
                      <th className="text-left font-medium py-2 px-2">Case (State)</th>
                      <th className="text-left font-medium py-2 px-2">GM ID</th>
                      <th className="text-left font-medium py-2 px-2">Ref</th>
                      {DELTA_FIELDS.map(f => (
                        <th
                          key={f.key}
                          className="text-right font-medium py-2 px-2 whitespace-nowrap">
                          {f.label}
                        </th>
                      ))}
                      <th className="py-2 px-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {cases.map(c => (
                      <tr
                        key={c.gmId}
                        className={`border-b border-gray-100 dark:border-neutral-70 ${
                          c.highlighted ? 'bg-amber-50 dark:bg-amber-900/20' : ''
                        }`}>
                        <td className="py-1 px-2 font-medium dark:text-neutral-10">
                          <span className="flex items-center gap-2 min-w-0">
                            <i
                              role="button"
                              title={c.highlighted ? 'Remove highlight' : 'Highlight this row'}
                              onClick={() => toggleHighlight(c.gmId)}
                              className={`pi ${
                                c.highlighted
                                  ? 'pi-star-fill !text-amber-500'
                                  : 'pi-star text-gray-400'
                              } cursor-pointer hover:!text-amber-500`}
                            />
                            <span className="truncate" title={c.stateName || c.gmId}>
                              {c.stateName || c.gmId}
                            </span>
                          </span>
                        </td>
                        <td className="py-1 px-2 text-gray-500 dark:text-neutral-60">{c.gmId}</td>
                        <td className="py-1 px-2 text-gray-500 dark:text-neutral-60">
                          {c.refGmId ?? '—'}
                        </td>
                        {DELTA_FIELDS.map(f => {
                          const v = c.delta[f.key]
                          return (
                            <td
                              key={f.key}
                              className={`py-1 px-2 text-right tabular-nums ${deltaColorClass(
                                f.key,
                                v
                              )}`}>
                              {fmtDelta(f.key, v)}
                            </td>
                          )
                        })}
                        <td className="py-1 px-1">
                          <div className="flex items-center justify-end">
                            <Button
                              icon="pi pi-trash"
                              text
                              severity="danger"
                              className="!p-1"
                              tooltip="Remove case from report"
                              tooltipOptions={{ position: 'left', showDelay: 400 }}
                              onClick={() => removeCase(c.gmId)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Sections */}
          <div className="dark:bg-neutral-80 bg-white p-4 rounded-md border border-gray-200 dark:border-neutral-70">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold dark:text-neutral-10">
                Comments, Recommendations &amp; Media
              </h3>
              <div className="flex items-center gap-2">
                <Button
                  label="Mark-Up"
                  icon="pi pi-pencil"
                  outlined
                  disabled={!markupImages.length}
                  tooltip={
                    markupImages.length
                      ? undefined
                      : 'Add images to the report first to mark them up'
                  }
                  tooltipOptions={{ position: 'top' }}
                  onClick={() => setShowMarkup(true)}
                />
                <Button
                  label="Add Section"
                  icon="pi pi-plus"
                  outlined
                  onClick={() => setSections(prev => [...prev, newSection()])}
                  disabled={!userCanMutate}
                />
              </div>
            </div>

            <div className="space-y-4">
              {sections.map((s, i) => (
                <div
                  key={s.id}
                  id={s.anchorId}
                  onDragOver={e => {
                    if (!dragItemRef.current) return
                    e.preventDefault()
                    if (dragOverSectionId !== s.id) setDragOverSectionId(s.id)
                  }}
                  onDragLeave={e => {
                    // Ignore moves within the section's own children.
                    if (e.currentTarget.contains(e.relatedTarget as Node)) return
                    if (dragOverSectionId === s.id) setDragOverSectionId(null)
                  }}
                  onDrop={e => {
                    const from = dragItemRef.current
                    setDragOverSectionId(null)
                    if (!from) return
                    e.preventDefault()
                    moveMedia(from, s.id)
                    dragItemRef.current = null
                  }}
                  className={classNames(
                    'border rounded-md p-3 space-y-3 transition-colors scroll-mt-4',
                    s.anchorId && 'border-t-4 !border-t-gm-blue mt-6',
                    dragOverSectionId === s.id && dragItemRef.current?.sectionId !== s.id
                      ? 'border-gm-blue border-dashed ring-2 ring-gm-blue/40'
                      : 'border-gray-200 dark:border-neutral-70'
                  )}>
                  {s.anchorId && (
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-neutral-50">
                      Report
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <InputText
                      value={s.heading}
                      onChange={e => updateSection(s.id, { heading: e.target.value })}
                      placeholder={`Section ${i + 1} heading`}
                      className={classNames('flex-1 font-semibold', s.anchorId && '!text-base')}
                    />
                    <Button
                      icon="pi pi-images"
                      label="Attach Media"
                      outlined
                      disabled={cases.length === 0}
                      tooltip={cases.length === 0 ? 'Add a case first' : undefined}
                      onClick={() => openPicker(s.id)}
                    />
                    {sections.length > 1 && (
                      <Button
                        icon="pi pi-trash"
                        text
                        severity="danger"
                        tooltip="Delete this section and its media"
                        tooltipOptions={{ position: 'left' }}
                        onClick={() => removeSection(s.id)}
                      />
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <span className="flex flex-col gap-1">
                      <label className="text-sm text-gray-600 dark:text-neutral-20">
                        Comments (one per line)
                      </label>
                      <InputTextarea
                        value={s.commentsText}
                        onChange={e => updateSection(s.id, { commentsText: e.target.value })}
                        rows={4}
                        autoResize
                        placeholder={'On average was fairly numb\nNo massive impacts on CLF'}
                      />
                    </span>
                    <span className="flex flex-col gap-1">
                      <label className="text-sm text-gray-600 dark:text-neutral-20">
                        Recommendation
                      </label>
                      <InputTextarea
                        value={s.recommendation}
                        onChange={e => updateSection(s.id, { recommendation: e.target.value })}
                        rows={4}
                        autoResize
                        placeholder="Did seem to help with separation…"
                      />
                    </span>
                  </div>

                  {s.deltaMetrics && s.deltaMetrics.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-gray-500 dark:text-neutral-60 border-b border-gray-200 dark:border-neutral-70">
                            <th className="py-1 pr-2">Case</th>
                            <th className="py-1 pr-2">GM ID</th>
                            <th className="py-1 pr-2">Ref</th>
                            {DELTA_FIELDS.map(f => (
                              <th key={f.key} className="py-1 px-2 text-right">
                                {f.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {s.deltaMetrics.map((row, ri) => (
                            <tr
                              key={ri}
                              className={classNames(
                                'border-b border-gray-100 dark:border-neutral-70',
                                { 'bg-amber-50 dark:bg-amber-900/10': row.highlight }
                              )}>
                              <td className="py-1 pr-2 dark:text-neutral-10">{row.caseName}</td>
                              <td className="py-1 pr-2 text-gray-500 dark:text-neutral-60">
                                {row.gm_id ?? ''}
                              </td>
                              <td className="py-1 pr-2 text-gray-500 dark:text-neutral-60">
                                {row.ref_gm_id ?? ''}
                              </td>
                              {DELTA_FIELDS.map(f => (
                                <td
                                  key={f.key}
                                  className={`py-1 px-2 text-right tabular-nums ${deltaColorClass(
                                    f.key,
                                    row[f.key]
                                  )}`}>
                                  {fmtDelta(f.key, row[f.key])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {(s.images.length > 0 || s.gifs.length > 0) && (
                    <div className="flex flex-wrap gap-2">
                      {[
                        ...s.images.map(m => ({ ...m, kind: 'images' as const })),
                        ...s.gifs.map(m => ({ ...m, kind: 'gifs' as const }))
                      ].map(m => (
                        <div
                          key={m.url}
                          draggable
                          onDragStart={e => {
                            dragItemRef.current = { sectionId: s.id, kind: m.kind, url: m.url }
                            e.dataTransfer.effectAllowed = 'move'
                          }}
                          onDragEnd={() => {
                            dragItemRef.current = null
                            setDragOverSectionId(null)
                          }}
                          title="Drag to move to another section"
                          className="relative w-28 h-20 rounded overflow-hidden border border-gray-200 dark:border-neutral-70 group cursor-move">
                          {m.type === 'video' ? (
                            <video
                              src={m.url}
                              muted
                              preload="metadata"
                              draggable={false}
                              className="w-full h-full object-cover bg-black pointer-events-none"
                            />
                          ) : m.geomBlobPath ? (
                            <GeometryProxyImage
                              blobPath={m.geomBlobPath}
                              gmid={m.geomGmid}
                              alt={m.caption}
                            />
                          ) : (
                            <img
                              src={m.url}
                              alt={m.caption}
                              draggable={false}
                              className="w-full h-full object-cover pointer-events-none"
                            />
                          )}
                          <button
                            className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-5 h-5 text-xs opacity-0 group-hover:opacity-100"
                            title="Remove from this section"
                            onClick={() => removeMedia(s.id, m.kind, m.url)}>
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Generate + result */}
          <div className="dark:bg-neutral-80 bg-white p-4 rounded-md border border-gray-200 dark:border-neutral-70 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Button
                label="Preview Report"
                icon="pi pi-eye"
                outlined
                onClick={handlePreview}
                loading={previewing}
              />
              <Button
                label="Download HTML"
                icon="pi pi-download"
                outlined
                onClick={handleDownload}
                loading={downloading}
              />
              <Button
                label="Export to PPT"
                icon="pi pi-file-export"
                outlined
                onClick={handleExportPptx}
                loading={exportingPptx}
              />
              {editingReportId ? (
                <Button
                  label="Save Report"
                  icon="pi pi-save"
                  onClick={handleSave}
                  loading={saving || generating}
                  tooltip="Save changes to this published report"
                  tooltipOptions={{ position: 'top' }}
                  disabled={!userCanMutate}
                />
              ) : (
                <Button
                  label="Publish Report"
                  icon="pi pi-file"
                  onClick={handleGenerate}
                  loading={generating}
                  tooltip="Generate and publish this report for the team"
                  tooltipOptions={{ position: 'top' }}
                  disabled={!userCanMutate}
                />
              )}
              {generating && (
                <span className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-20">
                  <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" />
                  {job?.status === 'processing' ? 'Building report…' : 'Queued…'}
                </span>
              )}
            </div>

            {job?.status === 'completed' && job.resultUrl && (
              <div className="flex items-center gap-2 p-3 rounded bg-blue-50 dark:bg-neutral-70 border border-blue-200 dark:border-neutral-60">
                <i className="pi pi-check-circle text-green-600" />
                <a
                  href={job.resultUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-gm-blue underline truncate flex-1">
                  {job.resultUrl}
                </a>
                <Button icon="pi pi-copy" label="Copy" text onClick={copyLink} />
                <Button
                  icon="pi pi-external-link"
                  label="Open"
                  tooltip="View the published report in a new tab"
                  tooltipOptions={{ position: 'top' }}
                  onClick={() => window.open(job.resultUrl, '_blank', 'noreferrer')}
                />
              </div>
            )}
          </div>
        </>
      )}

      {/* Media picker dialog */}
      <Dialog
        header="Attach Media"
        visible={pickerSectionId !== null}
        onHide={() => setPickerSectionId(null)}
        style={{ width: '70vw' }}
        modal>
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <label className="text-sm text-gray-600 dark:text-neutral-20">Case</label>
          <Dropdown
            value={pickerCaseId}
            options={caseOptions}
            onChange={e => {
              setPickerCaseId(e.value)
              setPickerFilters(null)
              resetPickerFilters()
            }}
            placeholder="Select a case"
            className="w-56"
            tooltip="Choose a case to browse its media"
            tooltipOptions={{ position: 'top' }}
          />
          <Dropdown
            value={pickerImageType}
            options={pickerImageTypeOptions}
            optionLabel="label"
            optionValue="value"
            onChange={e => {
              setPickerImageType(e.value)
              setPickerSectionFilter(null)
              setPickerLocation(null)
              setPickerMetricTypes([])
            }}
            placeholder="Type"
            className="text-sm w-36"
            tooltip="Filter images by type"
            tooltipOptions={{ position: 'top' }}
          />
          {pickerImageType === 'Sect' && (
            <Dropdown
              value={pickerSectionFilter}
              options={pickerSectionOptions}
              optionLabel="label"
              optionValue="value"
              onChange={e => setPickerSectionFilter(e.value)}
              placeholder="Section"
              className="text-sm w-36"
              tooltip="Filter images by section"
              tooltipOptions={{ position: 'top' }}
            />
          )}
          {pickerImageType === 'Surface' && (
            <Dropdown
              value={pickerLocation}
              options={pickerLocationOptions}
              optionLabel="label"
              optionValue="value"
              onChange={e => setPickerLocation(e.value)}
              placeholder="Location"
              className="text-sm w-36"
              tooltip="Filter images by surface location"
              tooltipOptions={{ position: 'top' }}
            />
          )}
          <MetricMultiSelect
            value={pickerMetricTypes}
            options={pickerMetricTypeOptions}
            onChange={setPickerMetricTypes}
            className="text-sm w-36"
          />
          {pickerLoading && <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" />}
          <span className="flex-1" />
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={e => handleUploadFiles(e.target.files)}
          />
          <Button
            label="Upload"
            icon="pi pi-upload"
            onClick={() => uploadInputRef.current?.click()}
            loading={uploadingMedia}
            tooltip="Upload local images or videos to this run"
            tooltipOptions={{ position: 'top' }}
          />
        </div>

        <h4 className="font-semibold mb-2 dark:text-neutral-10">Images</h4>
        {pickerImages.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-neutral-60 mb-4">
            No images for this case.
          </p>
        ) : (
          <div className="grid grid-cols-4 gap-2 mb-4 max-h-72 overflow-y-auto">
            {pickerImages.map(img => {
              const selected = pickerSection?.images.some(m => m.url === img.url)
              return (
                <button
                  key={img.fullPath}
                  onClick={() => attachImage(img)}
                  title={selected ? 'Already in this section' : 'Add to this section'}
                  className={classNames(
                    'relative rounded overflow-hidden border-2',
                    selected ? 'border-gm-blue' : 'border-transparent hover:border-gray-300'
                  )}>
                  <img src={img.url} alt={img.name} className="w-full h-24 object-cover" />
                  {selected && (
                    <span className="absolute top-1 right-1 bg-gm-blue text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
                      ✓
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        <h4 className="font-semibold mb-2 dark:text-neutral-10">GIFs</h4>
        {visibleGifs.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-neutral-60">
            No completed GIFs for this run.
          </p>
        ) : (
          <div className="grid grid-cols-4 gap-2 max-h-60 overflow-y-auto">
            {visibleGifs.map(gif => {
              const selected = pickerSection?.gifs.some(m => m.url === gif.resultUrl)
              return (
                <button
                  key={gif.jobId}
                  onClick={() => attachGif(gif)}
                  title={selected ? 'Already in this section' : 'Add to this section'}
                  className={classNames(
                    'relative rounded overflow-hidden border-2',
                    selected ? 'border-gm-blue' : 'border-transparent hover:border-gray-300'
                  )}>
                  <img src={gif.resultUrl} alt={gif.name} className="w-full h-24 object-cover" />
                  {selected && (
                    <span className="absolute top-1 right-1 bg-gm-blue text-white rounded-full w-5 h-5 text-xs flex items-center justify-center">
                      ✓
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button label="Done" onClick={() => setPickerSectionId(null)} />
        </div>
      </Dialog>

      <MarkupModal
        isOpen={showMarkup}
        onClose={() => setShowMarkup(false)}
        images={markupImages}
        onSave={handleMarkupSave}
        saving={markupSaving}
      />

      <Dialog
        header="New Summary Report"
        visible={showSummaryModal}
        onHide={() => setShowSummaryModal(false)}
        style={{ width: '46rem' }}
        modal>
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-neutral-20">
            Consolidate published reports into a weekly summary. Adjust the timeframe and choose
            which reports to include.
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-sm text-gray-600 dark:text-neutral-20">Timeframe</label>
            <Calendar
              value={summaryRange as Date[]}
              onChange={e => setSummaryRange((e.value as (Date | null)[]) ?? [null, null])}
              selectionMode="range"
              readOnlyInput
              showButtonBar
              dateFormat="mm/dd/yy"
              placeholder="Select a date range"
              className="w-72"
            />
          </div>

          <div className="border border-gray-200 dark:border-neutral-70 rounded-md max-h-80 overflow-y-auto">
            {summaryLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-20 p-4">
                <ProgressSpinner style={{ width: 20, height: 20 }} strokeWidth="4" /> Loading…
              </div>
            ) : summaryInRange.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-neutral-60 p-4">
                No published reports in this timeframe.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-neutral-70">
                {summaryInRange.map(r => {
                  const runNames = Array.from(
                    new Set(
                      (r.payload?.cases ?? []).map(c => c.runName).filter((n): n is string => !!n)
                    )
                  )
                  return (
                    <li key={r.reportId} className="flex items-center gap-3 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={summarySelectedIds.has(r.reportId)}
                        onChange={() => toggleSummaryReport(r.reportId)}
                        className="h-4 w-4 accent-gm-blue"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm dark:text-neutral-10 truncate" title={r.title}>
                          {r.title}
                        </div>
                        <div
                          className="text-xs text-gray-500 dark:text-neutral-60 truncate"
                          title={runNames.join(', ') || '—'}>
                          {runNames.join(', ') || '—'} · {fmtDate(r.timeUpdated)}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600 dark:text-neutral-20">
              {summaryInRange.filter(r => summarySelectedIds.has(r.reportId)).length} selected
            </span>
            <div className="flex items-center gap-2">
              <Button label="Cancel" outlined onClick={() => setShowSummaryModal(false)} />
              <Button
                label="Create Summary"
                icon="pi pi-check"
                onClick={createSummary}
                tooltip={
                  summaryInRange.filter(r => summarySelectedIds.has(r.reportId)).length === 0
                    ? 'Select at least one report'
                    : 'Consolidate the selected reports into a summary'
                }
                tooltipOptions={{ position: 'top' }}
                disabled={
                  !userCanMutate ||
                  summaryLoading ||
                  summaryInRange.filter(r => summarySelectedIds.has(r.reportId)).length === 0
                }
              />
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default ReportGenerator
